/**
 * Game audio (#260): the sound cue catalog (game-content/data/sounds.json)
 * and the cue references content carries (npcs.json, items.json, the
 * Essence abilities). Content names cues by id; the catalog says which
 * files a cue plays and how. See docs/gameplay/audio.md.
 */

/** Every mix channel a cue can play on; each has its own volume setting. */
export const SOUND_CHANNELS = ['music', 'ambience', 'effects', 'ui'] as const;

/** The mix channel a cue plays on. */
export type SoundChannel = typeof SOUND_CHANNELS[number];

/** One named sound in the catalog. */
export interface SoundCueDefinition {
    readonly channel: SoundChannel;
    /** Files under `/assets/`, one picked at random per play. */
    readonly files: readonly string[];
    /** 0..1 gain before the channel's volume. Defaults to 1. */
    readonly volume?: number;
    /** Playback-rate range, picked per play so repeats differ. Defaults to `[1, 1]`. */
    readonly pitch?: readonly [number, number];
    /** Loops until stopped (ambience beds, channels). */
    readonly isLooping?: boolean;
    /** How many plays of this cue may sound at once; the oldest is cut for a new one. Defaults to 4. */
    readonly maxVoices?: number;
    /** Plays closer together than this are dropped (a volley of hits in one frame). Defaults to 0. */
    readonly minIntervalMs?: number;
}

/** game-content/data/sounds.json, as `/assets/sounds` serves it. */
export interface SoundCatalog {
    readonly version: number;
    readonly cues: Readonly<Record<string, SoundCueDefinition>>;
}

/** An NPC's own cues (npcs.json `sounds`); any left out fall back to its weapon's and the defaults. */
export interface NpcSoundSet {
    /** Its swing, in place of its weapon class's. */
    readonly attack?: string;
    /** When a blow lands on it, after the blow's own impact. */
    readonly hurt?: string;
    /** When it is defeated, in place of `npc-death`. */
    readonly death?: string;
}

/** A weapon's own cues (items.json `sounds`); any left out fall back to its weapon class's. */
export interface WeaponSoundSet {
    readonly swing?: string;
    readonly hit?: string;
}

/** An Essence ability's cues (see `ESSENCE_ABILITIES`); any left out fall back to the generic Essence cues. */
export interface AbilitySoundSet {
    /** A press-and-trigger ability's gather. */
    readonly windup?: string;
    /** It goes off. */
    readonly release?: string;
    /** Its blow lands. */
    readonly hit?: string;
    /** Its burst (Immolate's nova, Unveil's ring). */
    readonly burst?: string;
    /** Its heal lands. */
    readonly heal?: string;
    /** Loops while it is channelled (Draw). */
    readonly channel?: string;
}

/** A volume setting: the master volume or one channel's. */
export type AudioVolumeId = 'master' | SoundChannel;

/** The account's audio settings: each volume 0..1, and whether all sound is muted. */
export type AudioSettings = Record<AudioVolumeId, number> & { readonly isMuted: boolean };

/** Where a sound library file lives: the downloaded packs (source-assets) or the files the game ships (assets). */
export type SoundLibraryRoot = 'source' | 'game';

/** A sound file the Sound Debugger can audition (developers only; see `/api/sound-library`). */
export interface SoundLibraryEntry {
    readonly root: SoundLibraryRoot;
    /** Its path under its root's audio folder, with `/` separators. */
    readonly path: string;
    /** The folder it sits in under the root: a pack (`kenney-impact-sounds`) or a game folder (`weapons`). */
    readonly group: string;
    readonly sizeBytes: number;
}

/** A sound library group's license file (`/api/sound-library/license`): its path under the root and its text. */
export interface SoundLibraryLicense {
    readonly file: string;
    readonly text: string;
}
